export type SessionMode = 'REAL' | 'ONLINE';
export type SessionRole = 'host' | 'player' | 'viewer';
export type SessionPhase =
  | 'home'
  | 'add-players'
  | 'setup'
  | 'lobby'
  | 'order-reveal'
  | 'in-game'
  | 'game-over';

interface SessionExtras {
  lobbyPhase?: 'LOBBY' | 'ORDER_REVEAL' | 'IN_GAME' | 'POST_GAME';
  lastPlayerId?: string | null;
  groupToken?: string | null;
  roomId?: string | null;
  currentRoundId?: string | null;
  wasOfflineSaved?: boolean;
}

export interface SessionState {
  version: 1;
  mode: SessionMode;
  gameId: string;
  roomId?: string | null;
  currentRoundId?: string | null;
  role: SessionRole;
  phase: SessionPhase;
  lastPath: string;
  updatedAt: string;
  extras?: SessionExtras;
}

import { resolveOnlineIdentity } from './onlineIdentity';

const SESSION_KEY = 'dice1k_session';
const MODE_KEY = 'dice1k-mode';
const PERSIST_KEYS = new Set([
  'dice1k_theme',
  'dice1k-language',
  'dice1k-sound-enabled',
  'dice1k-haptics-enabled',
  'dice1k_emote_slots', // shared emote storage
  'dice1k_group_token',
  'dice1k_group_last_game',
]);
const PERSIST_PREFIXES: string[] = [];

function safeParse(raw: string | null): SessionState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.version !== 1) return null;
    if (!parsed.mode || !parsed.phase || typeof parsed.lastPath !== 'string') return null;
    const extras = parsed?.extras && typeof parsed.extras === 'object'
      ? parsed.extras as SessionExtras
      : undefined;
    const identity = resolveOnlineIdentity({
      gameId: typeof parsed?.gameId === 'string' ? parsed.gameId : '',
      roomId: typeof parsed?.roomId === 'string' ? parsed.roomId : extras?.roomId,
      currentRoundId: typeof parsed?.currentRoundId === 'string' ? parsed.currentRoundId : extras?.currentRoundId,
      groupToken: extras?.groupToken,
    });
    const normalizedGameId = typeof parsed?.gameId === 'string'
      ? parsed.gameId.trim().toUpperCase()
      : '';
    return {
      ...(parsed as SessionState),
      gameId: normalizedGameId,
      roomId: identity.roomId,
      currentRoundId: identity.currentRoundId,
      ...(extras
        ? {
            extras: {
              ...extras,
              roomId: identity.roomId,
              currentRoundId: identity.currentRoundId,
            },
          }
        : {}),
    } satisfies SessionState;
  } catch {
    return null;
  }
}

export function readSession(): SessionState | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return safeParse(localStorage.getItem(SESSION_KEY));
  } catch {
    return null;
  }
}

export function writeSession(next: SessionState): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const identity = resolveOnlineIdentity({
      gameId: next.gameId,
      roomId: next.roomId || next.extras?.roomId,
      currentRoundId: next.currentRoundId || next.extras?.currentRoundId,
      groupToken: next.extras?.groupToken,
    });
    const normalizedGameId = (next.gameId || '').trim().toUpperCase();
    const normalizedSession: SessionState = {
      ...next,
      gameId: normalizedGameId,
      roomId: identity.roomId,
      currentRoundId: identity.currentRoundId,
      extras: next.extras
        ? {
            ...next.extras,
            roomId: identity.roomId,
            currentRoundId: identity.currentRoundId,
          }
        : next.extras,
    };
    localStorage.setItem(SESSION_KEY, JSON.stringify(normalizedSession));
    localStorage.setItem(MODE_KEY, normalizedSession.mode);
  } catch {
    // ignore persistence failures
  }
}

export function resolveSessionIdentity(session: SessionState | null | undefined): {
  roomId: string | null;
  currentRoundId: string | null;
} {
  const identity = resolveOnlineIdentity({
    gameId: session?.gameId,
    roomId: session?.roomId || session?.extras?.roomId,
    currentRoundId: session?.currentRoundId || session?.extras?.currentRoundId,
    groupToken: session?.extras?.groupToken,
  });
  return {
    roomId: identity.roomId,
    currentRoundId: identity.currentRoundId,
  };
}

export function clearSession(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}

export function hardResetStorage(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (PERSIST_KEYS.has(key)) continue;
      if (PERSIST_PREFIXES.some((prefix) => key.startsWith(prefix))) continue;
      if (key.startsWith('dice1k')) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

export function fullResetPersistentState(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    hardResetStorage();
    clearSession();
  } catch {
    // ignore
  }
}
